import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import Layout from '../components/Layout';
import { cancelClassProfile, fetchClassProfileCalendar } from '../database/databaseClassProfile.js';
import { fetchProfileNotifications, notifyWaitlistForClass } from '../database/databaseNotifications.js';

const hhmm = (t) => (t ? String(t).slice(0, 5) : '');

export default function Profile() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [settings, setSettings] = useState(null);

  // notifications
  const [notes, setNotes] = useState([]);
  const [openNotif, setOpenNotif] = useState(false);

  // classes
  const [classes, setClasses] = useState([]);
  const [openClasses, setOpenClasses] = useState(false);

  // in-page toast (optional, used when redirected with state)
  const [toast, setToast] = useState(null);
  const location = useLocation();

  /* ---------------- Auth + profile ---------------- */
  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.auth.getUser();
      if (error) {
        console.error('Error fetching user:', error.message);
        return;
      }
      setUser(data.user ?? null);

      if (data.user) {
        const { data: prof, error: pErr } = await supabase.from('profiles').select('full_name, phone').eq('id', data.user.id).single();
        if (pErr) console.error('Error fetching profile:', pErr.message);
        else setProfile(prof);
      }

      const { data: settingsData, error: settingsError } = await supabase.from('settings').select('*');
      if (settingsError) console.error('Error fetching settings:', settingsError.message);
      else setSettings(settingsData);
    })();
  }, []);

  /* ---------------- Toast via router state ---------------- */
  useEffect(() => {
    if (location.state?.toast) {
      setToast(location.state.toast);
      const t = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(t);
    }
  }, [location.state]);

  /* ---------------- Notifications (with left join) ---------------- */
  const unreadCount = useMemo(() => notes.filter((n) => !n.read_at).length, [notes]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await fetchProfileNotifications(user.id);

      // Insert missing link rows for this user
      const missing = (data || [])
        .filter((n) => !n.notificationsprofile)
        .map((n) => ({
          user_id: user.id,
          notification_id: n.id,
          read_at: null,
        }));

      if (missing.length > 0) {
        const { error: insertErr } = await supabase.from('notificationsprofile').insert(missing);
        if (insertErr) console.error('Error inserting missing links:', insertErr.message);
      }

      // Normalize state
      setNotes(
        (data || []).map((n) => ({
          id: n.id,
          message: n.message,
          datetime: n.datetime,
          read_at: n.notificationsprofile[0]?.read_at ?? null,
        })),
      );
    })();
  }, [user]);

  // mark all as read when opening the collapsible
  const toggleNotif = async () => {
    const willOpen = !openNotif;
    setOpenNotif(willOpen);
    if (willOpen && unreadCount > 0 && user) {
      const nowIso = new Date().toISOString();

      // Find all unread notifications
      const unread = notes.filter((n) => !n.read_at);
      if (unread.length > 0) {
        const rows = unread.map((n) => ({
          user_id: user.id,
          notificationid: n.id,
          read_at: nowIso,
        }));

        const { error } = await supabase.from('notificationsprofile').insert(rows);

        if (error) {
          console.error('Error inserting notificationsprofile:', error.message);
        } else {
          setNotes((prev) => prev.map((n) => (unread.some((u) => u.id === n.id) ? { ...n, read_at: nowIso } : n)));
        }
      }
    }
  };

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await fetchClassProfileCalendar(user.id, false);
      setClasses(data || []);
    })();
  }, [user]);

  const handleCancel = async (row) => {
    const classDateTime = new Date(`${row.date}T${row.starttime}`);
    const now = new Date();
    const diffHours = (classDateTime - now) / (1000 * 60 * 60);

    if (diffHours < (settings[0]?.cancel_cutoff_hours ?? 0)) {
      alert(`Solo puedes cancelar clases con ${settings[0]?.cancel_cutoff_hours} horas o más de anticipación.`);
      return;
    }

    if (!window.confirm('¿Estas seguro que quieres eliminar esta clase?')) return;

    const record = await cancelClassProfile(row.id);
    if (record && !record.waiting_list) {
      notifyWaitlistForClass(record.calendar_class, row.title, `${row.date} ${hhmm(row.starttime)}`);
    }

    setClasses((prev) => prev.filter((c) => c.id !== row.id));
    alert('Clase cancelada correctamente.');
  };

  const confirmAsistance = async (id) => {
    const { data, error } = await supabase.rpc('confirm_attendance', { p_classprofile_id: id });

    if (error) {
      alert('Error confirmando la asistencia: ' + error.message);
      return;
    }

    if (!data) {
      // function returned false → no spots left
      alert('Lo siento, ya no hay cupos disponibles.');
      return;
    }

    alert('Asistencia confirmada correctamente.');

    setClasses((prev) => prev.filter((c) => c.calendarclass?.id !== id)); // no funciona TODO
  };

  return (
    <Layout page="profile">
      <section className="bg-cream py-16 px-6 md:px-20 font-sans">
        <div className="max-w-7xl mx-auto mt-12">
          {/* Toast inside header area */}
          {toast && <div className="mb-6 px-4 py-2 rounded bg-terracotta text-cream shadow">{toast}</div>}

          {/* Unread banner */}
          {unreadCount > 0 && (
            <div className="mb-6 px-4 py-2 rounded bg-sand text-terracotta-dark border border-terracotta/30">
              Tienes <strong>{unreadCount}</strong> notificación
              {unreadCount > 1 ? 'es' : ''} sin leer. Ábre el panel de <strong>notificaciones</strong> para verlas.
            </div>
          )}

          {/* Profile info */}
          <div className="space-y-4 mb-8 text-ink">
            <div className="border-b border-sand pb-2">
              <span className="block text-[11px] font-semibold tracking-[0.3em] uppercase text-terracotta mb-1">Nombre</span>
              {profile?.full_name ?? '—'}
            </div>
            <div className="border-b border-sand pb-2">
              <span className="block text-[11px] font-semibold tracking-[0.3em] uppercase text-terracotta mb-1">Email</span>
              {user?.email ?? '—'}
            </div>
            <div className="border-b border-sand pb-2">
              <span className="block text-[11px] font-semibold tracking-[0.3em] uppercase text-terracotta mb-1">Teléfono</span>
              {profile?.phone ?? '—'}
            </div>
          </div>
        </div>

        {/* Notifications collapsible */}
        <div className="border border-sand rounded-lg max-w-7xl mx-auto mt-6 overflow-hidden">
          <button onClick={toggleNotif} className="flex justify-between items-center px-4 py-3 bg-sand text-terracotta-dark w-full">
            <span className="font-semibold flex items-center gap-2">
              NOTIFICACIONES
              {unreadCount > 0 && (
                <span className="inline-flex items-center justify-center text-xs font-semibold bg-terracotta text-cream rounded-full px-2 py-[2px]">
                  {unreadCount}
                </span>
              )}
            </span>
            <svg className={`w-5 h-5 transform transition ${openNotif ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {openNotif && (
            <div className="px-4 py-3 space-y-3">
              {notes.length === 0 ? (
                <p className="text-sm text-ink">No hay notificaciones.</p>
              ) : (
                notes.map((n) => (
                  <div key={n.id} className={`text-sm text-ink border-b border-sand pb-2 ${n.read_at ? 'opacity-75' : 'font-medium'}`}>
                    <p>{n.message}</p>
                    <span className="text-xs text-ink/70">{n.datetime ? new Date(n.datetime).toLocaleDateString() : ''}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Reserved classes collapsible */}
        <div className="border border-sand rounded-lg max-w-7xl mx-auto mt-6 overflow-hidden">
          <button onClick={() => setOpenClasses((v) => !v)} className="flex justify-between items-center px-4 py-3 bg-sand text-terracotta-dark w-full">
            <span className="font-semibold">Clases reservadas</span>
            <svg className={`w-5 h-5 transform transition ${openClasses ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {openClasses && (
            <div className="overflow-x-auto">
              {!classes || classes.length === 0 ? (
                <p className="text-sm text-ink px-4 py-3">No tienes clases reservadas.</p>
              ) : (
                <table className="min-w-full text-sm text-left text-ink">
                  <thead className="bg-sand text-terracotta-dark uppercase">
                    <tr>
                      <th className="px-6 py-3">Clase</th>
                      <th className="px-6 py-3">Fecha</th>
                      <th className="px-6 py-3">Hora</th>
                      <th className="px-6 py-3">Comentario</th>
                      <th className="px-6 py-3">Estado</th>
                      <th className="px-6 py-3">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {classes.map((row) => {
                      const time = row.starttime && row.endtime ? `${hhmm(row.starttime)} – ${hhmm(row.endtime)}` : '—';
                      const status = row.canceled_at ? 'Cancelada' : row.waiting_list ? 'En lista de espera' : 'Confirmada';
                      return (
                        <tr key={row.id} className="bg-card border-b border-sand hover:bg-cream">
                          <td className="px-6 py-4">{row.title}</td>
                          <td className="px-6 py-4">{row.date}</td>
                          <td className="px-6 py-4">{time}</td>
                          <td className="px-6 py-4">{row.comment}</td>
                          <td className="px-6 py-4">{status}</td>
                          <td className="px-6 py-4">
                            {row.canceled_at === null && (
                              <button onClick={() => handleCancel(row)} className="text-red-600 hover:underline mr-4">
                                Cancelar
                              </button>
                            )}
                            {status === 'En lista de espera' && row.confirmed_count < row.maxattendees && (
                              <button onClick={() => confirmAsistance(row.id)} className="text-terracotta hover:underline">
                                Confirmar asistencia
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
